import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppToast from "@toothfairy/shared-ui/AppToast";
import React, { useMemo, useState } from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import Constants from "../../../Constants";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppMultipleSelectInput from "@toothfairy/shared-ui/AppMultipleSelectInput";
import AppInputLabel from "@toothfairy/shared-ui/AppInputLabel";
import useWorkspaces from "../../../Hooks/useWorkspaces";

const availableLayers = [
  { id: "k_proj", type: "all" },
  { id: "w2", type: "mistral" },
  { id: "w1", type: "mistral" },
  { id: "gate", type: "mistral" },
  { id: "w3", type: "mistral" },
  { id: "o_proj", type: "all" },
  { id: "q_proj", type: "all" },
  { id: "v_proj", type: "all" },
  { id: "up_proj", type: "other" },
  { id: "down_proj", type: "other" },
  { id: "gate_proj", type: "other" },
];

// Structural keys that are not hyperparameters — dropped from the advanced
// JSON overrides so a pasted config can't rename the log or flip datasetOnly.
const ADVANCED_JSON_BLOCKLIST = ["name", "description", "datasetOnly"];

// Formik-connected fields. Runs INSIDE AppForms.AppForm so sliders, dropdowns
// and the JSON editor all read/write through formik values (QA-settings
// pattern), and Start merges those values over the stored trainingConfig.
const FineTuningConfigFields = ({
  trainingConfig,
  trainingLogId,
  handleFineTuningStart = () => {},
}) => {
  const { getTrainingLogInfo } = useWorkspaces();
  const { currentTheme, mode } = AppTheme.useTheme();
  const { state } = AppStateManager.useAppStateManager();
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { values, setFieldValue } = AppForms.useFormikContext();
  const [advancedJsonErrorCount, setAdvancedJsonErrorCount] = useState(0);

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.border_color,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
      borderRadius: 10,
      color: currentTheme?.theme?.font_color,
    }),
    input: (base) => ({
      ...base,
      color: currentTheme?.theme?.font_color, // This would change the color of the text inside the input
    }),
    option: (styles, { data }) => {
      let color = currentTheme?.theme?.font_color;
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.transparent,
        color: currentTheme?.theme?.font_color,
        cursor: "pointer",

        ":active": {
          ...styles[":active"],
          backgroundColor: currentTheme?.theme?.primary,
        },
        ":hover": {
          ...styles[":hover"],
          backgroundColor: currentTheme?.theme?.primary,
        },
      };
    },
    singleValue: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
    }),
    multiValue: (styles, { data }) => {
      let color = currentTheme?.theme?.font_color;
      return {
        ...styles,
        backgroundColor: currentTheme?.theme?.primary,
      };
    },
    multiValueLabel: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
    }),
    multiValueRemove: (styles, { data }) => ({
      ...styles,
      color: currentTheme?.theme?.font_color,
      ":hover": {
        backgroundColor: currentTheme?.theme?.primary,
        color: "white",
      },
    }),
    menu: (styles) => ({
      ...styles,
      backgroundColor: currentTheme?.theme?.inputBackgroundColor,
      borderColor: currentTheme?.theme?.lightGray,
      fontFamily: "normalFont",
      fontSize: currentTheme?.theme?.small_font_size,
    }),
  };

  // Merge the advanced JSON overrides over the form values and start.
  // The overrides live on the formik field `advancedJsonConfig` (an object
  // kept valid by the JSON editor's onValidate) and are stripped after merge.
  const handleStartWithAdvancedConfig = () => {
    if (advancedJsonErrorCount > 0) {
      AppToasty.show(t("training.config.advancedJsonConfigInvalid"), {
        type: "danger",
        placement: "top",
      });
      return;
    }
    const { advancedJsonConfig, ...formValues } = values || {};
    let overrides = {};
    if (
      advancedJsonConfig != null &&
      (typeof advancedJsonConfig !== "object" ||
        Array.isArray(advancedJsonConfig))
    ) {
      AppToasty.show(t("training.config.advancedJsonConfigNotObject"), {
        type: "danger",
        placement: "top",
      });
      return;
    }
    if (advancedJsonConfig && typeof advancedJsonConfig === "object") {
      overrides = { ...advancedJsonConfig };
      const ignoredKeys = ADVANCED_JSON_BLOCKLIST.filter((k) => k in overrides);
      if (ignoredKeys.length > 0) {
        AppToasty.show(
          t("training.config.advancedJsonConfigIgnoredKeys", {
            keys: ignoredKeys.join(", "),
          }),
          { type: "warning", placement: "top" }
        );
        ADVANCED_JSON_BLOCKLIST.forEach((k) => delete overrides[k]);
      }
      if (Object.keys(overrides).length > 0) {
        AppToasty.show(
          t("training.config.advancedJsonConfigApplied", {
            count: Object.keys(overrides).length,
          }),
          { type: "success", placement: "top" }
        );
      }
    }
    const { advancedJsonOverrides, ...storedConfig } = trainingConfig || {};
    let finalValues = { ...formValues };
    // RLVR (grpoTraining + rl.enabled): fold the flat rl* form fields into
    // the nested `rl` object the backend expects and drop the flat keys.
    if (
      getTrainingLogInfo(trainingLogId)?.trainingConfig?.rl?.enabled === true
    ) {
      const pipPackages = String(formValues.rlPipPackages || "")
        .split("\n")
        .map((p) => p.trim())
        .filter(Boolean)
        .slice(0, 50);
      const rl = {
        enabled: true,
        env: "tfcode",
        numGenerations: formValues.rlNumGenerations || 8,
        maxToolCallingIterations: formValues.rlMaxToolCallingIterations || 16,
        entropyCoef: formValues.rlEntropyCoef ?? 0.05,
      };
      if (pipPackages.length > 0) rl.pipPackages = pipPackages;
      finalValues = { ...finalValues, rl };
      delete finalValues.rlNumGenerations;
      delete finalValues.rlMaxToolCallingIterations;
      delete finalValues.rlEntropyCoef;
      delete finalValues.rlPipPackages;
    }
    handleFineTuningStart(trainingLogId, {
      ...storedConfig,
      ...finalValues,
      ...overrides,
    });
  };

  return (
    <View
      style={{
        marginLeft: 20,
        maxWidth: 450,
        paddingLeft: 5,
      }}
    >
      {getTrainingLogInfo(trainingLogId)?.type == "conversationalTraining" ? (
        <>
          {" "}
          <AppForms.AppFormTrackSlider
            step={0.000001}
            maximumValue={0.0005}
            minimumValue={0.000001}
            visualMultiplier={100}
            unitLabel="%"
            label={t("training.config.learningRate")}
            name="learningRateRC"
          />
          <AppForms.AppFormTrackSlider
            step={0.01}
            maximumValue={1}
            minimumValue={0.01}
            visualMultiplier={100}
            unitLabel="%"
            label={t("training.config.learningRatePeakRatio")}
            name="peakLearningRateRC"
          />
          <AppForms.AppFormTrackSlider
            step={0.01}
            maximumValue={1}
            minimumValue={0.01}
            visualMultiplier={100}
            unitLabel="%"
            label={t("training.config.learningRateWarmupRatio")}
            name="warmUpLearningRateRC"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={5000}
            minimumValue={1}
            label={t("training.config.minDatasetForTrainingTest")}
            name="datasetTestSizeThresholdRC"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={100}
            minimumValue={1}
            unitLabel="%"
            label={t("training.config.testDatasetSize")}
            name="testSizeRC"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={100}
            minimumValue={1}
            label={t("training.config.numberOfEvaluations")}
            name="evaluationNumberRC"
          />
          <AppForms.AppFormTrackSlider
            maximumValue={20}
            minimumValue={1}
            label={t("training.config.maxEpochs")}
            name="trainingEpochsRC"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={20}
            minimumValue={1}
            label={t("training.config.numberOfCheckpoints")}
            name="checkpointsNumberRC"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={
              (state?.trainableModels || [])?.find(
                (u) => u?.id == values?.baseModel
              )?.maxBatchSize || 32
            }
            minimumValue={8}
            label={t("training.config.batchSize")}
            name="batchSizeRC"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={64}
            minimumValue={1}
            label={t("training.config.loraRank")}
            name="loraRRC"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={16}
            minimumValue={1}
            label={t("training.config.loraAlpha")}
            name="loraAlphaRC"
          />
          <AppForms.AppFormTrackSlider
            step={0.1}
            maximumValue={1}
            minimumValue={0.1}
            visualMultiplier={100}
            unitLabel="%"
            label={t("training.config.loraDropout")}
            name="loraDropoutRC"
          />
          <View
            style={{
              zIndex: 10,
              marginBottom: 10,
            }}
          >
            <AppInputLabel
              style={[{ marginLeft: 10, marginBottom: 10 }]}
              isMandatory={false}
              label={t("training.config.selectLoraLayers")}
            />
            <View style={{ marginLeft: 5 }}>
              <AppMultipleSelectInput
                accessibilityLabel="ft_layers"
                onSelectionChange={(v) => {
                  setFieldValue("loraLayersRC", v);
                }}
                labelOption={"label"}
                options={availableLayers
                  ?.filter((u) => u?.type == "all" || u?.type == "other")
                  ?.map((u) => {
                    return {
                      ...u,
                      value: u.id,
                      label: u?.id,
                    };
                  })}
                groupByProperty="type"
                selectedItems={values?.loraLayersRC || []}
                placeholder={t("training.config.filterLayers")}
                isAsync={false}
                uniqueKey="id"
                showDropDowns={true}
                readOnlyHeadings={true}
                styles={colourStyles}
              />
            </View>
          </View>
          <AppDropDown
            showTextInputLabel={true}
            label={t("training.config.trainOnInputs")}
            inputLabelStyle={{
              marginLeft: 0,
            }}
            wrapperContainer={{
              marginLeft: 10,
            }}
            dropDownStyle={{
              minWidth: 100,
              borderColor: currentTheme?.theme?.border_color,
              maxWidth: 250,
            }}
            isElevated={false}
            dropdownIconColor="transparent"
            items={[
              { id: "auto", name: t("training.config.auto") },
              { id: true, name: t("training.config.true") },
              { id: false, name: t("training.config.false") },
            ]}
            propertyLabel="name"
            propertyValue="id"
            selectedValue={values?.trainOnInputsRC}
            onValueChange={(itemValue, itemIndex) => {
              setFieldValue("trainOnInputsRC", itemValue);
            }}
          />
        </>
      ) : getTrainingLogInfo(trainingLogId)?.type == "generativeTraining" ? (
        <>
          <AppForms.AppFormTrackSlider
            step={0.05}
            maximumValue={0.9}
            minimumValue={0.1}
            label={t("training.config.dpoBeta")}
            name="dpoBetaRC"
          />
        </>
      ) : getTrainingLogInfo(trainingLogId)?.type == "grpoTraining" &&
        getTrainingLogInfo(trainingLogId)?.trainingConfig?.rl?.enabled ? (
        <>
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={16}
            minimumValue={4}
            label={t("training.config.rlNumGenerations", {
              defaultValue: "RL generations per task (G)",
            })}
            name="rlNumGenerations"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={32}
            minimumValue={4}
            label={t("training.config.rlMaxToolCallingIterations", {
              defaultValue: "Max tool calls per episode",
            })}
            name="rlMaxToolCallingIterations"
          />
          <AppForms.AppFormTrackSlider
            step={0.01}
            maximumValue={0.5}
            minimumValue={0}
            label={t("training.config.rlEntropyCoef", {
              defaultValue: "Entropy bonus",
            })}
            name="rlEntropyCoef"
          />
          <AppForms.AppFormField
            isMandatory={false}
            label={t("training.config.rlPipPackages", {
              defaultValue: "Extra pip packages (one per line)",
            })}
            description={t("training.config.rlPipPackagesHelp", {
              defaultValue:
                "PEP 508 specifiers only (e.g. pandas>=2.0), installed into the training container at start. Max 50.",
            })}
            name="rlPipPackages"
            multiline
          />
        </>
      ) : getTrainingLogInfo(trainingLogId)?.type == "grpoTraining" ? (
        <>
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={16}
            minimumValue={2}
            label={t("training.config.grpoNumGenerations")}
            name="grpoNumGenerations"
          />
          <AppForms.AppFormTrackSlider
            step={64}
            maximumValue={2048}
            minimumValue={128}
            label={t("training.config.grpoMaxCompletionLength")}
            name="grpoMaxCompletionLength"
          />
          <AppDropDown
            label={t("training.config.grpoRewardFuncs")}
            items={[
              {
                id: "default",
                label: t("training.config.rewardFunctions.default"),
              },
              {
                id: "format_compliance",
                label: t("training.config.rewardFunctions.formatCompliance"),
              },
              {
                id: "length_penalty",
                label: t("training.config.rewardFunctions.lengthPenalty"),
              },
              {
                id: "default,format_compliance",
                label: t(
                  "training.config.rewardFunctions.defaultFormatCompliance"
                ),
              },
            ]}
            propertyLabel="label"
            propertyValue="id"
            selectedValue={values?.grpoRewardFuncs || "default"}
            onValueChange={(itemValue) => {
              setFieldValue("grpoRewardFuncs", itemValue);
            }}
          />
        </>
      ) : getTrainingLogInfo(trainingLogId)?.type == "ktoTraining" ? (
        <>
          <AppForms.AppFormTrackSlider
            step={0.05}
            maximumValue={0.9}
            minimumValue={0.01}
            label={t("training.config.ktoBeta")}
            name="ktoBeta"
          />
          <AppForms.AppFormTrackSlider
            step={0.1}
            maximumValue={4.0}
            minimumValue={0.1}
            label={t("training.config.ktoDesirableWeight")}
            name="ktoDesirableWeight"
          />
          <AppForms.AppFormTrackSlider
            step={0.1}
            maximumValue={4.0}
            minimumValue={0.1}
            label={t("training.config.ktoUndesirableWeight")}
            name="ktoUndesirableWeight"
          />
        </>
      ) : getTrainingLogInfo(trainingLogId)?.type == "continuedPretraining" ? (
        <>
          <AppForms.AppFormTrackSlider
            step={0.000001}
            maximumValue={0.001}
            minimumValue={0.000001}
            label={t("training.config.cptLearningRate")}
            name="learningRateCPT"
          />
          <AppForms.AppFormTrackSlider
            step={1}
            maximumValue={10}
            minimumValue={1}
            label={t("training.config.cptEpochs")}
            name="trainingEpochsCPT"
          />
        </>
      ) : (
        <></>
      )}

      <View
        style={{
          zIndex: -1,
          marginBottom: 10,
          marginTop: 10,
        }}
      >
        <AppForms.AppFormFieldJSONV2
          showTextInputLabel
          isMandatory={false}
          label={t("training.config.advancedJsonConfig")}
          description={t("training.config.advancedJsonConfigHelp")}
          name="advancedJsonConfig"
          mode="code"
          darkMode={mode === "dark"}
          wrapperContainer={{
            height: 350,
          }}
          textInputLabelStyle={{
            marginLeft: 10,
            marginTop: 5,
          }}
          onValidate={(json) => {
            const errors = [];
            if (json == null) return errors;
            if (typeof json !== "object" || Array.isArray(json)) {
              errors.push({
                message: t("training.config.advancedJsonConfigNotObject"),
              });
            }
            setAdvancedJsonErrorCount(errors.length);
            return errors;
          }}
        />
      </View>

      <ToothFairyButton
        wrapperStyle={{
          marginTop: 20,
          alignSelf: "center",
          minWidth: 120,
        }}
        title={t("training.config.start")}
        onPress={handleStartWithAdvancedConfig}
      />
    </View>
  );
};

const FineTuningConfigSelection = ({
  trainingConfig,
  setTrainingConfig = () => {},
  trainingLogId,
  handleFineTuningStart = () => {},
}) => {
  const { getTrainingLogInfo } = useWorkspaces();

  // Frozen at mount so the JSON editor's `json` prop (taken from formik
  // initialValues) never re-initialises while the user is editing.
  const initialValues = useMemo(
    () => ({
      learningRateRC:
        trainingConfig?.learningRateRC || Constants.learningRateRC,
      peakLearningRateRC:
        trainingConfig?.peakLearningRateRC || Constants.peakLearningRate,
      warmUpLearningRateRC:
        trainingConfig?.warmUpLearningRateRC || Constants.warmUpLearningRate,
      datasetTestSizeThresholdRC:
        trainingConfig?.datasetTestSizeThresholdRC ||
        Constants.datasetTestSizeThresholdRC,
      testSizeRC: trainingConfig?.testSizeRC || Constants.testSizeRC * 100,
      evaluationNumberRC:
        trainingConfig?.evaluationNumberRC || Constants.evaluationNumber,
      trainingEpochsRC:
        trainingConfig?.trainingEpochsRC || Constants.baseTrainingRCEpochs,
      checkpointsNumberRC:
        trainingConfig?.checkpointsNumberRC || Constants.checkpointsNumber,
      batchSizeRC: trainingConfig?.batchSizeRC || 8,
      loraRRC: trainingConfig?.loraRRC || Constants.loraR,
      loraAlphaRC: trainingConfig?.loraAlphaRC || Constants.loraAlpha,
      loraDropoutRC: trainingConfig?.loraDropoutRC || Constants.loraDropout,
      loraLayersRC: trainingConfig?.loraLayersRC || Constants.loraLayers || [],
      trainOnInputsRC: trainingConfig?.trainOnInputsRC,
      baseModel: getTrainingLogInfo(trainingLogId)?.trainingConfig?.baseModel,
      // DPO
      dpoBetaRC: trainingConfig?.dpoBetaGN || Constants.dpoBetaGN,
      // GRPO
      grpoNumGenerations: trainingConfig?.grpoNumGenerations || 4,
      grpoMaxCompletionLength: trainingConfig?.grpoMaxCompletionLength || 512,
      grpoRewardFuncs: trainingConfig?.grpoRewardFuncs || "default",
      // KTO
      ktoBeta: trainingConfig?.ktoBeta || 0.1,
      ktoDesirableWeight: trainingConfig?.ktoDesirableWeight || 1.0,
      ktoUndesirableWeight: trainingConfig?.ktoUndesirableWeight || 1.0,
      // CPT
      learningRateCPT: trainingConfig?.learningRateCPT || 0.00005,
      trainingEpochsCPT: trainingConfig?.trainingEpochsCPT || 3,
      // Advanced JSON overrides (object — kept valid by the editor)
      advancedJsonConfig: trainingConfig?.advancedJsonOverrides || {},
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trainingLogId]
  );

  return (
    <AppForms.AppForm initialValues={initialValues} onSubmit={() => {}}>
      <FineTuningConfigFields
        trainingConfig={trainingConfig}
        trainingLogId={trainingLogId}
        handleFineTuningStart={handleFineTuningStart}
      />
    </AppForms.AppForm>
  );
};

export default FineTuningConfigSelection;
